import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  formatRank,
  getLeaderboardView,
  LEADERBOARD_VIEWS,
  rankPlayers,
  type LeaderboardPlayer,
  type LeaderboardResponse,
  type LeaderboardViewKey,
} from '../../systems/leaderboard';
import { fetchLeaderboard } from '../../systems/leaderboard-api';
import { useWorkspace } from '../../systems/workspace-context';
import { useAuth } from '../../systems/auth-context';
import { Spinner } from '../spinner';
import { PlayerAvatar } from './player-avatar';
import { LeaderboardRow } from './leaderboard-row';
import styles from './leaderboard-screen.module.css';

const VIEW_STORAGE_KEY = 'vn_leaderboard_view';

function readStoredView(): LeaderboardViewKey {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (LEADERBOARD_VIEWS.some((view) => view.key === stored)) return stored as LeaderboardViewKey;
  } catch {
    // Stockage indisponible : premier onglet.
  }
  return 'complet';
}

function RankBadge({ rank, first }: { rank: number; first?: boolean }) {
  const { number, suffix } = formatRank(rank);
  return (
    <span className={`${styles.rankBadge}${first ? ` ${styles.rankBadgeFirst}` : ''}`}>
      {number}
      {suffix}
    </span>
  );
}

/** Écran B du zoning : le joueur, les quatre onglets, le podium et la liste à partir du 4e. */
export function LeaderboardScreen() {
  const { workspaceId } = useWorkspace();
  const { user } = useAuth();
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [error, setError] = useState('');
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [viewKey, setViewKey] = useState<LeaderboardViewKey>(readStoredView);
  const listRef = useRef<HTMLUListElement>(null);
  const myRowRef = useRef<HTMLLIElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    fetchLeaderboard(scenarioId)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Impossible de charger le classement.');
      });
    return () => {
      cancelled = true;
    };
  }, [scenarioId, workspaceId]);

  const view = getLeaderboardView(viewKey);
  const ranked = useMemo(() => (data ? rankPlayers(data.players, viewKey) : []), [data, viewKey]);
  const myIndex = ranked.findIndex((player) => player.isMe);

  // La liste s'ouvre défilée jusqu'à la ligne du joueur.
  useLayoutEffect(() => {
    const list = listRef.current;
    const row = myRowRef.current;
    if (!list) return;
    if (!row) {
      list.scrollTop = 0;
      return;
    }
    list.scrollTop = Math.max(0, row.offsetTop - list.clientHeight + row.offsetHeight + 10);
  }, [ranked]);

  const selectView = useCallback((key: LeaderboardViewKey) => {
    setViewKey(key);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, key);
    } catch {
      // Préférence non retenue : sans gravité.
    }
  }, []);

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const moves: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const move = moves[event.key];
    if (move === undefined) return;
    event.preventDefault();
    const next = (index + move + LEADERBOARD_VIEWS.length) % LEADERBOARD_VIEWS.length;
    const target = LEADERBOARD_VIEWS[next];
    if (!target) return;
    selectView(target.key);
    tabRefs.current[next]?.focus();
  };

  const shieldsMax = data?.shieldsMax ?? 0;
  // Avant la réponse, la gélule montre déjà le profil connu de la session.
  const me = data?.me ?? (user?.pseudo ? { pseudo: user.pseudo, color: user.avatar_color ?? '', ranked: false, entry: null } : undefined);
  const meEntry: LeaderboardPlayer | null = (myIndex >= 0 ? ranked[myIndex] : undefined) ?? me?.entry ?? null;
  const summary = meEntry ? view.summary(meEntry, shieldsMax) : null;

  const step = (player: LeaderboardPlayer | undefined, place: 1 | 2 | 3) => (
    <li
      key={place}
      className={`${styles.step} ${styles[`p${place}`]} verre${player ? '' : ` ${styles.stepEmpty}`}`}
      aria-label={player ? undefined : `${place === 1 ? '1re' : `${place}e`} place libre`}
    >
      {player && (
        <>
          <PlayerAvatar pseudo={player.pseudo} color={player.color} className={styles.stepAvatar} />
          <RankBadge rank={place} first={place === 1} />
          <span className={styles.stepName}>{player.pseudo}</span>
          <span className={styles.stepValue}>{view.value(player, shieldsMax)}</span>
        </>
      )}
    </li>
  );

  return (
    <div className={styles.screen}>
      <section className={styles.stage} aria-labelledby="leaderboard-title">
        <h1 id="leaderboard-title" className={styles.srOnly}>
          Classement
        </h1>

        <div className={styles.top}>
          <div className={`${styles.me} verre`}>
            <PlayerAvatar pseudo={me?.pseudo ?? '?'} color={me?.color} size={62} />
            <div className={styles.meText}>
              <p className={styles.meName}>{me?.pseudo ?? 'Sans pseudo'}</p>
              <p className={styles.meValue}>
                {me?.ranked && summary && myIndex >= 0 ? (
                  <>
                    {summary.before}
                    <b>{summary.strong}</b>
                    {summary.after} · {formatRank(myIndex + 1).number}
                    <sup>{formatRank(myIndex + 1).suffix}</sup>
                  </>
                ) : data ? (
                  'Hors classement'
                ) : (
                  ' '
                )}
              </p>
            </div>
          </div>

          <div className={styles.controls}>
            <div className={`${styles.tabs} verre`} role="tablist" aria-label="Classements">
              {LEADERBOARD_VIEWS.map((entry, index) => {
                const selected = entry.key === viewKey;
                return (
                  <button
                    key={entry.key}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`leaderboard-tab-${entry.key}`}
                    aria-selected={selected}
                    aria-controls="leaderboard-panel"
                    tabIndex={selected ? 0 : -1}
                    className={styles.tab}
                    onClick={() => selectView(entry.key)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                  >
                    {entry.label}
                  </button>
                );
              })}
            </div>
            {data && data.scenarios.length > 1 && (
              <label className={styles.scenarioPicker}>
                <span className="etiq">Campagne</span>
                <select value={data.scenarioId ?? ''} onChange={(event) => setScenarioId(event.target.value)}>
                  {data.scenarios.map((scenario) => (
                    <option key={scenario.id} value={scenario.id}>
                      {scenario.title}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>

        <div id="leaderboard-panel" role="tabpanel" aria-labelledby={`leaderboard-tab-${viewKey}`} className={styles.panel}>
          {error ? (
            <p className={`${styles.message} verre`} role="alert">
              {error}
            </p>
          ) : !data ? (
            <Spinner layout="centered" label="Chargement du classement…" />
          ) : !data.scenarioId ? (
            <p className={`${styles.message} verre`}>Aucune campagne n&apos;est encore ouverte dans ce workspace.</p>
          ) : ranked.length === 0 ? (
            <p className={`${styles.message} verre`}>Personne n&apos;est encore classé.</p>
          ) : (
            <>
              <ol className={styles.podium} aria-label={`Podium : ${view.label}`}>
                {step(ranked[1], 2)}
                {step(ranked[0], 1)}
                {step(ranked[2], 3)}
              </ol>

              <div className={`${styles.list} verre`}>
                <div className={styles.listHeader}>
                  <span className="etiq-sable">{view.label}</span>
                  <span className={styles.total}>
                    {ranked.length} {ranked.length > 1 ? 'joueurs' : 'joueur'}
                  </span>
                </div>
                {ranked.length > 3 ? (
                  <ul ref={listRef} className={styles.rows} aria-label={`Suite du classement : ${view.label}`}>
                    {ranked.slice(3).map((player, index) => (
                      <LeaderboardRow
                        key={`${player.pseudo}-${index}`}
                        ref={player.isMe ? myRowRef : undefined}
                        rank={index + 4}
                        pseudo={player.pseudo}
                        color={player.color}
                        value={view.value(player, shieldsMax)}
                        highlighted={player.isMe}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className={styles.listEmpty}>Le reste du classement apparaîtra ici.</p>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
